import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Paper,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { isAfter, isSameMonth, subMonths } from 'date-fns';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchInvoices, setInvoices, updateInvoiceStatus } from '../store/slices/invoiceSlice';
import { fetchBrokers } from '../store/slices/brokerSlice';
import {
  calculateDemurrageCharge,
  calculateFreightRev,
  calculateFuelSurcharge,
  calculateTotalCharges
} from '../config/calculations';
import { NewPaymentData } from '../types/payment';
import { computePendingMetrics } from '../utils/invoiceMetrics';
import { monthLabel, toMillis } from '../utils/dates';
import { round2 } from '../utils/money';
import { matchesDestination, uniqueDestinations } from '../utils/destinations';
import { downloadInvoiceExcel, downloadInvoicePdf } from '../services/invoiceExport';
import StatCards from '../components/pendingLoads/StatCards';
import OverdueInvoicesButton from '../components/pendingLoads/OverdueInvoicesButton';
import InvoiceFilters from '../components/pendingLoads/InvoiceFilters';
import InvoiceTable from '../components/pendingLoads/InvoiceTable';
import PayoutQueue, { PayoutQueueGroup } from '../components/pendingLoads/PayoutQueue';
import InvoiceDetailsDialog from '../components/pendingLoads/InvoiceDetailsDialog';
import EditInvoiceDialog from '../components/pendingLoads/EditInvoiceDialog';
import {
  ColumnVisibility,
  DEFAULT_COLUMNS,
  DEFAULT_FILTERS,
  DriverDirectory,
  InvoiceFilterState,
  PendingInvoice,
  SortState,
  driverInfoFor
} from '../components/pendingLoads/types';

const QUEUE_WIDTH = 400;

const TERMINALS = ['Tesoro', 'Kinder Morgan', 'Chevron'];

interface DeleteDialogProps {
  open: boolean;
  invoiceNumber: string;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmationDialog: React.FC<DeleteDialogProps> = ({
  open,
  invoiceNumber,
  onClose,
  onConfirm
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Delete Invoice</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to delete invoice #{invoiceNumber}? This action cannot be undone.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const PendingLoads: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAppSelector((state) => state.auth);
  const {
    items: invoices,
    loading: invoicesLoading,
    error: invoicesError
  } = useAppSelector((state) => state.invoices);
  const { items: brokers } = useAppSelector((state) => state.brokers);

  const isAdmin = user?.accountType === 'admin';
  const isDriver = user?.accountType === 'driver';

  const [filters, setFilters] = useState<InvoiceFilterState>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [columns, setColumns] = useState<ColumnVisibility>(DEFAULT_COLUMNS);
  const [groupByMonth, setGroupByMonth] = useState(true);

  const [driverDirectory, setDriverDirectory] = useState<DriverDirectory>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [queueHidden, setQueueHidden] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [viewInvoice, setViewInvoice] = useState<any>(null);
  const [editingInvoice, setEditingInvoice] = useState<PendingInvoice | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  /* ------------------------------- data loading ------------------------------ */

  useEffect(() => {
    dispatch(fetchBrokers());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      dispatch(fetchInvoices({ isAdmin: user.accountType === 'admin', dogtag: user.dogtag }));
    }
  }, [dispatch, user]);

  // Names and percentage cuts for every driver, so the table and the payout
  // queue can show people rather than dogtags.
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const loadDrivers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        if (cancelled) return;

        const directory: DriverDirectory = {};
        snapshot.docs.forEach((userDoc) => {
          const data = userDoc.data();
          if (!data.dogtag) return;
          directory[data.dogtag] = {
            dogtag: data.dogtag,
            name: data.name || data.displayName || data.dogtag,
            percentageCut: data.percentageCut || 0
          };
        });
        setDriverDirectory(directory);
      } catch (error) {
        // Drivers can be denied read access to the roster; the dogtag stands in.
        console.error('Error loading driver directory:', error);
      }
    };

    loadDrivers();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // A search handed over from the header lands in the query string.
  useEffect(() => {
    const term = searchParams.get('q');
    if (term) {
      setFilters((prev) => ({ ...prev, search: term, status: 'all' }));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /* -------------------------------- filtering -------------------------------- */

  const updateFilters = (patch: Partial<InvoiceFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  /**
   * One predicate behind both the table and the headline numbers, so the cards
   * always reconcile with the rows on screen. `ignoreStatus` lets the cards keep
   * counting pending invoices while the table shows whatever status is selected.
   */
  const matchesFilters = useCallback(
    (invoice: any, options: { ignoreStatus?: boolean } = {}) => {
      const search = filters.search.trim().toLowerCase();
      const loadNumber = filters.loadNumber.trim().toLowerCase();
      const minAmount = filters.minAmount === '' ? null : parseFloat(filters.minAmount);
      const maxAmount = filters.maxAmount === '' ? null : parseFloat(filters.maxAmount);
      const now = new Date();

      if (search) {
        const haystack = [
          invoice.invoiceNumber,
          invoice.orderNumber,
          invoice.loadNumber,
          invoice.destination
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (loadNumber && !(invoice.loadNumber || '').toLowerCase().includes(loadNumber)) {
        return false;
      }
      if (!matchesDestination(invoice.destination, filters.destination)) return false;

      if (!options.ignoreStatus && filters.status !== 'all' && invoice.status !== filters.status) {
        return false;
      }
      if (filters.driver !== 'all' && invoice.driver !== filters.driver) return false;
      if (filters.broker !== 'all' && invoice.broker !== filters.broker) return false;
      if (filters.terminal !== 'all' && invoice.terminal !== filters.terminal) return false;

      const amount = invoice.totalCharges || 0;
      if (minAmount !== null && !isNaN(minAmount) && amount < minAmount) return false;
      if (maxAmount !== null && !isNaN(maxAmount) && amount > maxAmount) return false;

      const created = toMillis(invoice.createdAt);
      if (!created) return filters.timeRange === 'all';
      const createdDate = new Date(created);

      switch (filters.timeRange) {
        case 'current':
          return isSameMonth(createdDate, now);
        case 'last3': {
          const threeMonthsAgo = subMonths(now, 3);
          return isAfter(createdDate, threeMonthsAgo) || isSameMonth(createdDate, threeMonthsAgo);
        }
        default:
          return true;
      }
    },
    [filters]
  );

  const filteredInvoices = useMemo(
    () => invoices.filter((invoice: any) => matchesFilters(invoice)),
    [invoices, matchesFilters]
  );

  const sortedInvoices = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...filteredInvoices].sort((a: any, b: any) => {
      switch (sort.key) {
        case 'invoiceNumber':
          return (
            direction * String(a.invoiceNumber || '').localeCompare(String(b.invoiceNumber || ''))
          );
        case 'totalCharges':
          return direction * ((a.totalCharges || 0) - (b.totalCharges || 0));
        default:
          return direction * (toMillis(a.createdAt) - toMillis(b.createdAt));
      }
    });
  }, [filteredInvoices, sort]);

  const pageRows = useMemo(
    () => sortedInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedInvoices, page, rowsPerPage]
  );

  // Keep the page in range when filtering shrinks the result set.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedInvoices.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [sortedInvoices.length, rowsPerPage, page]);

  // Month dividers describe the whole filtered month, not just the visible page.
  const monthSummaries = useMemo(() => {
    const summaries: Record<string, { count: number; total: number }> = {};
    sortedInvoices.forEach((invoice: any) => {
      const month = monthLabel(invoice.createdAt);
      const summary = summaries[month] || { count: 0, total: 0 };
      summary.count += 1;
      summary.total = round2(summary.total + (invoice.totalCharges || 0));
      summaries[month] = summary;
    });
    return summaries;
  }, [sortedInvoices]);

  // Same rows the table is showing (status aside), so filtering by broker moves
  // the headline numbers with it.
  const metrics = useMemo(
    () =>
      computePendingMetrics(
        invoices.filter((invoice: any) => matchesFilters(invoice, { ignoreStatus: true }))
      ),
    [invoices, matchesFilters]
  );

  const uniqueBrokers = useMemo(
    () => Array.from(new Set(brokers.map((broker: any) => broker.shortname))).sort(),
    [brokers]
  );

  const uniqueDrivers = useMemo(
    () =>
      Array.from(new Set(invoices.map((invoice: any) => invoice.driver).filter(Boolean))).sort(),
    [invoices]
  );

  const destinationOptions = useMemo(() => uniqueDestinations(invoices), [invoices]);

  /* ------------------------------ payout queue ------------------------------- */

  // A payout needs a pending invoice and someone to pay it to.
  const isRowSelectable = useCallback(
    (invoice: any) => invoice.status === 'pending' && Boolean(invoice.driver),
    []
  );

  const selectedInvoices = useMemo(
    () => invoices.filter((invoice: any) => selectedIds.includes(invoice.id)),
    [invoices, selectedIds]
  );

  // Drop selections whose invoice is gone or is no longer payable.
  useEffect(() => {
    setSelectedIds((prev) => {
      const payableIds = new Set(
        invoices.filter((invoice: any) => isRowSelectable(invoice)).map((invoice: any) => invoice.id)
      );
      const next = prev.filter((id) => payableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [invoices, isRowSelectable]);

  const queueGroups = useMemo<PayoutQueueGroup[]>(() => {
    const groups = new Map<string, PayoutQueueGroup>();

    selectedInvoices.forEach((invoice: any) => {
      const driver = driverInfoFor(driverDirectory, invoice.driver);
      const key = driver.dogtag || 'unassigned';
      const existing = groups.get(key);
      const group: PayoutQueueGroup =
        existing ||
        {
          driver,
          invoices: [],
          totalBeforeDeductions: 0,
          percentageCutAmount: 0,
          totalAfterDeductions: 0
        };

      group.invoices.push(invoice);
      group.totalBeforeDeductions = round2(
        group.totalBeforeDeductions + (invoice.totalCharges || 0)
      );
      group.percentageCutAmount = round2(
        group.totalBeforeDeductions * (driver.percentageCut / 100)
      );
      group.totalAfterDeductions = round2(
        group.totalBeforeDeductions - group.percentageCutAmount
      );

      groups.set(key, group);
    });

    return Array.from(groups.values()).sort((a, b) => a.driver.name.localeCompare(b.driver.name));
  }, [selectedInvoices, driverDirectory]);

  const queueTotalBefore = round2(
    queueGroups.reduce((sum, group) => sum + group.totalBeforeDeductions, 0)
  );
  const queueTotalAfter = round2(
    queueGroups.reduce((sum, group) => sum + group.totalAfterDeductions, 0)
  );
  const queueCuts = Array.from(new Set(queueGroups.map((group) => group.driver.percentageCut)));
  const uniformCut = queueCuts.length === 1 ? queueCuts[0] : null;

  const toggleRow = (invoice: any) => {
    setSelectedIds((prev) => {
      if (prev.includes(invoice.id)) return prev.filter((id) => id !== invoice.id);
      // Adding to an empty queue should bring the panel back into view.
      setQueueHidden(false);
      return [...prev, invoice.id];
    });
  };

  const togglePage = () => {
    const selectableIds = pageRows.filter(isRowSelectable).map((invoice: any) => invoice.id);
    const allSelected = selectableIds.every((id) => selectedIds.includes(id));
    setQueueHidden(false);
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !selectableIds.includes(id))
        : Array.from(new Set([...prev, ...selectableIds]))
    );
  };

  const selectAllFiltered = () => {
    setQueueHidden(false);
    setSelectedIds(sortedInvoices.filter(isRowSelectable).map((invoice: any) => invoice.id));
  };

  const handleSubmitQueue = async () => {
    setSubmitting(true);
    try {
      for (const group of queueGroups) {
        const payment: NewPaymentData = {
          createdAt: Timestamp.now(),
          driverName: group.driver.name,
          driverId: group.driver.dogtag,
          invoiceNumbers: group.invoices
            .map((invoice: any) => invoice.invoiceNumber)
            .filter((number: any): number is string => typeof number === 'string' && number.length > 0),
          totalBeforeDeductions: group.totalBeforeDeductions,
          totalAfterDeductions: group.totalAfterDeductions,
          percentageCut: group.driver.percentageCut,
          percentageCutAmount: group.percentageCutAmount,
          // Monthly deductions are no longer applied when a payout is created.
          monthlyDeductions: [],
          status: 'PENDING'
        };

        await addDoc(collection(db, 'payments'), payment);

        for (const invoice of group.invoices) {
          await dispatch(
            updateInvoiceStatus({ invoiceId: invoice.id, status: 'pay_to_driver' })
          ).unwrap();
        }
      }

      const invoiceCount = selectedIds.length;
      setSelectedIds([]);
      notify(
        `Submitted ${invoiceCount} invoice${invoiceCount === 1 ? '' : 's'} to ${queueGroups.length} driver${queueGroups.length === 1 ? '' : 's'}.`
      );

      if (user) {
        dispatch(fetchInvoices({ isAdmin: user.accountType === 'admin', dogtag: user.dogtag }));
      }
    } catch (error) {
      console.error('Error submitting payout queue:', error);
      notify('Failed to submit the payout queue. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /* -------------------------------- row actions ------------------------------ */

  const handleDownloadExcel = async (invoice: any) => {
    try {
      await downloadInvoiceExcel(invoice);
      notify('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      notify(
        `Error downloading invoice: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDownloadPdf = async (invoice: any) => {
    try {
      await downloadInvoicePdf(invoice);
      notify('PDF invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF invoice:', error);
      notify(
        `Error downloading PDF invoice: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;
    try {
      await deleteDoc(doc(db, 'invoices', invoiceToDelete.id));
      setSelectedIds((prev) => prev.filter((id) => id !== invoiceToDelete.id));
      setInvoiceToDelete(null);
      notify('Invoice deleted');

      if (user) {
        dispatch(fetchInvoices({ isAdmin: user.accountType === 'admin', dogtag: user.dogtag }));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      notify('Failed to delete the invoice. Please try again.', 'error');
    }
  };

  const handleEditSave = async (updatedInvoice: PendingInvoice) => {
    if (!updatedInvoice.id) {
      throw new Error('Invoice ID is required');
    }

    const freightRev = calculateFreightRev(updatedInvoice.gross || 0, updatedInvoice.rate || 0);
    const fuelSurcharge = calculateFuelSurcharge(
      freightRev,
      updatedInvoice.fuelSurchargePercent || 0
    );
    const demurrageCharge = calculateDemurrageCharge(updatedInvoice.totalDem || 0);
    // Total charges come from the form so a manual override survives the save.
    calculateTotalCharges(freightRev, fuelSurcharge, demurrageCharge);

    const invoiceToUpdate = {
      ...updatedInvoice,
      freightRev,
      fuelSurcharge,
      demurrageCharge,
      totalCharges: updatedInvoice.totalCharges,
      updatedAt: new Date()
    };

    await updateDoc(doc(db, 'invoices', updatedInvoice.id), invoiceToUpdate);

    dispatch(
      setInvoices(
        invoices.map((invoice: any) =>
          invoice.id === updatedInvoice.id ? { ...invoice, ...invoiceToUpdate } : invoice
        )
      )
    );

    setEditingInvoice(null);
    notify('Invoice updated');
  };

  /* --------------------------------- render ---------------------------------- */

  if (invoicesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (invoicesError) {
    return (
      <Box m={2}>
        <Alert severity="error">{invoicesError}</Alert>
      </Box>
    );
  }

  const queueVisible = isAdmin && selectedIds.length > 0 && !queueHidden;

  const queuePanel = (
    <PayoutQueue
      groups={queueGroups}
      invoiceCount={selectedIds.length}
      queueTotalBefore={queueTotalBefore}
      queueTotalAfter={queueTotalAfter}
      uniformCut={uniformCut}
      submitting={submitting}
      onRemoveInvoice={(invoice) =>
        setSelectedIds((prev) => prev.filter((id) => id !== invoice.id))
      }
      onCollapse={() => setQueueHidden(true)}
      onCancelAll={() => setSelectedIds([])}
      onSubmit={handleSubmitQueue}
    />
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!isDriver && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <OverdueInvoicesButton
                invoices={invoices as any[]}
                broker={filters.broker}
                onNotify={notify}
              />
            </Box>
          )}

          {!isDriver && <StatCards metrics={metrics} />}

          <InvoiceFilters
            filters={filters}
            onChange={updateFilters}
            onClear={() => {
              setFilters(DEFAULT_FILTERS);
              setPage(0);
            }}
            brokers={uniqueBrokers as string[]}
            terminals={TERMINALS}
            drivers={uniqueDrivers as string[]}
            destinations={destinationOptions}
            driverDirectory={driverDirectory}
            isAdmin={Boolean(isAdmin)}
          />

          <InvoiceTable
            rows={pageRows}
            filteredCount={sortedInvoices.length}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onTogglePage={togglePage}
            onSelectAllFiltered={selectAllFiltered}
            onClearSelection={() => setSelectedIds([])}
            selectionEnabled={Boolean(isAdmin)}
            isRowSelectable={isRowSelectable}
            sort={sort}
            onSortChange={setSort}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setPage(0);
            }}
            columns={columns}
            onColumnsChange={setColumns}
            groupByMonth={groupByMonth}
            onGroupByMonthChange={setGroupByMonth}
            monthSummaries={monthSummaries}
            driverDirectory={driverDirectory}
            showDriver={!isDriver}
            showTotals={!isDriver}
            canEdit={Boolean(isAdmin)}
            canDelete={Boolean(isAdmin)}
            onView={setViewInvoice}
            onEdit={(invoice) => setEditingInvoice(invoice as PendingInvoice)}
            onDownloadExcel={handleDownloadExcel}
            onDownloadPdf={handleDownloadPdf}
            onDelete={setInvoiceToDelete}
          />
        </Box>

        {/* Wide screens dock the queue beside the table; narrower ones use a drawer. */}
        {queueVisible && isWide && (
          <Paper
            variant="outlined"
            sx={{
              width: QUEUE_WIDTH,
              flexShrink: 0,
              position: 'sticky',
              top: 80,
              maxHeight: 'calc(100vh - 100px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {queuePanel}
          </Paper>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={queueVisible && !isWide}
        onClose={() => setQueueHidden(true)}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: QUEUE_WIDTH },
              display: 'flex',
              flexDirection: 'column'
            }
          }
        }}
      >
        {queuePanel}
      </Drawer>

      {/* Selection kept while the panel is hidden — this brings it back. */}
      {isAdmin && selectedIds.length > 0 && queueHidden && (
        <Badge
          badgeContent={selectedIds.length}
          color="primary"
          sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: theme.zIndex.drawer }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<ReceiptLongIcon />}
            onClick={() => setQueueHidden(false)}
          >
            Payout Queue
          </Button>
        </Badge>
      )}

      <InvoiceDetailsDialog
        open={Boolean(viewInvoice)}
        invoice={viewInvoice}
        driverDirectory={driverDirectory}
        onClose={() => setViewInvoice(null)}
      />

      <EditInvoiceDialog
        open={Boolean(editingInvoice)}
        invoice={editingInvoice}
        onClose={() => setEditingInvoice(null)}
        onSave={handleEditSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(invoiceToDelete)}
        invoiceNumber={invoiceToDelete?.invoiceNumber || ''}
        onClose={() => setInvoiceToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingLoads;
